import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useState } from "react";
import DefaultImg from "../../../../assets/img/default-image.svg";
import { TBlockUnBlockCandidatePayload } from "../../../../models/vacancy";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import { blockUnblockCandidateThunk } from "../../../../store/centers/thunks";
import CustomButton from "../../../UI/Buttons/CustomButton";
import TextArea from "../../../UI/Inputs/TextArea";
import PopupModal from "../../../UI/Popup";
import StarRating from "../../../UI/StarRating";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";

interface ConfirmBlockProps {
  showBlockModal: boolean;
  candidatesToBlock: Array<any>;
  onClose: () => void;
}

const ConfirmBlock: FC<ConfirmBlockProps> = ({
  showBlockModal,
  candidatesToBlock,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentCandidate = candidatesToBlock[currentIndex];
  const [error, setError] = useState("");

  const dispatch = useDispatch();
  const inProgress = useSelector((state) => state.Centres.inProgress);
  const [bulkRatings, setBulkRatings] = useState<
    Record<
      number,
      {
        rating?: number;
        isBlocked?: boolean;
        blockReason?: string;
      }
    >
  >({});

  const handleRemoveBlockCandidate = (id: number) => {
    if (currentIndex < candidatesToBlock.length - 1) {
      setCurrentIndex(currentIndex + 1); // Move to the next candidate
    } else {
      onClose();
    }
    setBulkRatings((prev) => {
      const newBulkRatings = { ...prev };
      delete newBulkRatings[id];
      return newBulkRatings;
    });
  };

  const handleBlockCandidate = async () => {
    const currentCandidate = candidatesToBlock[currentIndex];

    if (!currentCandidate) return;

    const blockReason = bulkRatings[currentCandidate.id]?.blockReason || "";

    if (!blockReason.trim()) {
      setError("Please provide a reason for blocking the candidate.");
      return;
    }

    const payload: TBlockUnBlockCandidatePayload = {
      action: "block",
      user_id:
        currentCandidate?.user?.id || currentCandidate?.selected_candidate?.id,
      reason: blockReason,
    };

    dispatch(showTransparentPreloaderAction());

    const res = await dispatch(blockUnblockCandidateThunk(payload));

    dispatch(hideTransparentPreloaderAction());
    setError(""); // Reset error for next candidate

    if (isFulfilled(res)) {
      document.body.classList.remove("modal-open");
      if (currentIndex < candidatesToBlock.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        dispatch(showNotificationAction("Done"));
        onClose(); // Close modal
      }
    } else {
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <PopupModal
        visible={showBlockModal}
        showCloseBtn={false}
        onClickClose={onClose}
        customWidth={650}
      >
        {currentCandidate && (
          <div className="d-flex flex-column gap-20">
            <div className="d-flex flex-column gap-15">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between">
                <div className="fs-22 fw-400 text-black">Block a candidate</div>
                <div className="fs-16 fw-400 text-grey-dark">
                  {candidatesToBlock.length > 1 &&
                    `${currentIndex + 1} of ${candidatesToBlock.length}`}
                </div>
              </div>
              <div className="d-flex gap-20 align-items-center">
                <div className="w-50px h-50px  radius-50px">
                  <img
                    alt="Candidate"
                    width="50px"
                    height="50px"
                    className="img-responsive rounded-circle object-fit-cover"
                    src={
                      currentCandidate?.user?.photo ||
                      currentCandidate?.selected_candidate?.photo ||
                      DefaultImg
                    }
                  />
                </div>
                <div className="flex-grow-1 text-break">
                  <div className="d-flex flex-column gap-3 text-black">
                    <div className="fs-16 fw-700">
                      {`${
                        currentCandidate?.user?.first_name ||
                        currentCandidate?.selected_candidate?.first_name
                      } ${
                        currentCandidate?.user?.last_name ||
                        currentCandidate?.selected_candidate?.last_name
                      }`}
                    </div>
                    <div className="d-flex gap-10 align-items-center">
                      <StarRating
                        rating={
                          currentCandidate?.selected_candidate?.rating ||
                          currentCandidate?.rating ||
                          0
                        }
                        color={
                          !currentCandidate?.selected_candidate?.rating
                            ? "#aaaaaa"
                            : "#51C2B9"
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column gap-8">
                <div className="fs-13 fw-400 text-black">
                  You are about to block{" "}
                  {`${
                    currentCandidate?.user?.first_name ||
                    currentCandidate?.selected_candidate?.first_name
                  } ${
                    currentCandidate?.user?.last_name ||
                    currentCandidate?.selected_candidate?.last_name
                  }`}
                  . Blocking them will mean your centres will not be visible to
                  them and they may not apply or accept your vacancies.
                </div>
                <div className="fs-13 fw-400 text-black">
                  Candidates will not be notified when they are blocked.
                </div>
                <div className="fs-13 fw-400 text-black">
                  Please include a reason as reference for other Centre Admin
                  and Centre Users in your team.
                </div>
              </div>
              <div className="d-flex flex-column gap-8">
                <TextArea
                  field={"comment"}
                  placeholder={"Enter a Reason"}
                  value={bulkRatings[currentCandidate.id]?.blockReason || ""}
                  onChange={(e) => {
                    setBulkRatings((prev) => ({
                      ...prev,
                      [currentCandidate.id]: {
                        ...prev[currentCandidate.id],
                        blockReason: e.target.value,
                      },
                    }));
                  }}
                />
              </div>
              {/* <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6">
                  <Warning />
                  <div className="fs-13 fw-400 text-black lh-sm">
                    The Centre Admin in your team are also managing the
                    following centres you are not assigned to: Bravo Centre,
                    Charlie Centre, Delta Centre. Blocking this candidate will
                    block them from these centres and your assigned centres.
                  </div>
                </div> */}
              {error && <ErrorMessage message={error} />}
            </div>
            <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between">
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-off-white text-black"
                onClick={() => handleRemoveBlockCandidate(currentCandidate)}
              >
                Don't Block
              </CustomButton>
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-alert text-white fw-700"
                onClick={handleBlockCandidate}
                isLoading={inProgress}
                disabled={
                  !bulkRatings[currentCandidate?.id]?.blockReason?.trim()
                    ?.length ||
                  (bulkRatings[currentCandidate?.id]?.blockReason?.trim()
                    ?.length ?? 0) < 5
                }
                contentClass="d-flex"
              >
                Confirm and Block
              </CustomButton>
            </div>
          </div>
        )}
      </PopupModal>
    </>
  );
};

export default ConfirmBlock;
